/**
 * Domain → IdP routing for the email-first login form.
 *
 * Artome emails (@artome.fi, @artomeusa.com) sign in via the Microsoft IdP
 * configured in Cognito (Entra OIDC federation, linked to the invited native
 * user by the PreSignUp Lambda trigger). Everyone else uses the Cognito-native
 * password flow.
 *
 * Single source of truth — also used by the admin Lambda's UserDto.auth_method
 * (server-side, in handler.ts) to label invites in the admin UI. Adding Google
 * for non-Artome users later is one line in `authMethodFor`; the call site in
 * AuthGate doesn't change.
 */

import type { AuthContextProps } from 'react-oidc-context';

export type AuthMethod = 'microsoft' | 'google' | 'password';

const ARTOME_DOMAINS = new Set(['artome.fi', 'artomeusa.com']);

export function authMethodFor(email: string): AuthMethod {
  const domain = email.toLowerCase().split('@')[1] ?? '';
  if (ARTOME_DOMAINS.has(domain)) return 'microsoft';
  return 'password';
}

/**
 * Remember the last email a user signed in with, so a returning user whose
 * session has fully lapsed gets a one-click "Continue as …" that routes
 * straight to the right IdP — no retyping, and never the wrong (native) form
 * for a federated Artome user (DE-182). Purely a convenience hint; the IdP
 * still authenticates. Storage is injectable so the helper is unit-testable
 * in the node test env (mirrors sanitizeReturnTo's `origin` param); it no-ops
 * safely when Web Storage is unavailable.
 */
const LAST_EMAIL_KEY = 'portal.lastEmail';

type ReadWriteStore = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStore(): ReadWriteStore | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined; // Web Storage can throw (disabled cookies / privacy mode)
  }
}

export function rememberEmail(
  email: string,
  store: ReadWriteStore | undefined = defaultStore(),
): void {
  const normalised = email.trim().toLowerCase();
  if (!normalised.includes('@')) return;
  try {
    store?.setItem(LAST_EMAIL_KEY, normalised);
  } catch {
    /* quota / disabled storage — remembering is best-effort */
  }
}

export function recalledEmail(
  store: ReadWriteStore | undefined = defaultStore(),
): string | null {
  try {
    const v = store?.getItem(LAST_EMAIL_KEY);
    return v && v.includes('@') ? v : null;
  } catch {
    return null;
  }
}

/**
 * Sanitise a post-login return path (DE-169). Resolves against the current
 * origin and REQUIRES the result be same-origin, then rebuilds a relative
 * path. This neutralises open-redirect vectors that naive prefix checks miss —
 * `//evil.com`, `/\evil.com` (browsers normalise back-slashes to protocol-
 * relative), `https://evil.com`, `javascript:` (opaque origin). Never the
 * callback route (would loop). Malformed / cross-origin → '/'.
 *
 * `origin` is injectable because vitest runs in the `node` env (no `window`):
 * the unit test passes an explicit origin; production uses the default.
 */
export function sanitizeReturnTo(
  raw: string | null | undefined,
  origin: string = window.location.origin,
): string {
  if (!raw) return '/';
  try {
    const u = new URL(raw, origin);
    if (u.origin !== origin) return '/';
    const path = u.pathname + u.search + u.hash;
    return path.startsWith('/auth/callback') ? '/' : path;
  } catch {
    return '/';
  }
}

/** The current location as a sanitised relative path, for post-login restore. */
export function currentReturnTo(): string {
  return sanitizeReturnTo(
    window.location.pathname + window.location.search + window.location.hash,
  );
}

/**
 * Redirect to Cognito's /oauth2/authorize with the right `identity_provider`
 * so the user lands directly on the correct upstream:
 *   - 'Microsoft' → Cognito redirects straight to login.microsoftonline.com
 *   - 'Google'    → Cognito redirects to Google (future)
 *   - 'COGNITO'   → Cognito Hosted UI password form
 *
 * `login_hint` pre-fills the email field downstream so the user doesn't retype.
 */
export function signinWith(
  method: AuthMethod,
  email: string,
  auth: AuthContextProps,
): Promise<void> {
  const idp =
    method === 'microsoft'
      ? 'Microsoft'
      : method === 'google'
        ? 'Google'
        : 'COGNITO';
  // Remember this email for a one-click return next time the session lapses.
  rememberEmail(email);
  return auth.signinRedirect({
    extraQueryParams: { identity_provider: idp, login_hint: email },
    // DE-169 — round-trip the page the user was on so onSigninCallback can
    // restore it instead of dumping everyone on the dashboard.
    state: { returnTo: currentReturnTo() },
  });
}
